import {Injectable} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';

import {MapboxGlService} from 'src/app/services/mapbox-gl.service';
import {MapboxglFeatureInfoComponent} from './mapboxgl-feature-info.component';
import {featureInfoPopupHeight, featureInfoPopupWidth, mapFields, TOOL, ToolTypes} from 'src/app/constants/map';
import {isNumber} from 'lodash';
import * as turf from '@turf/turf';

@Injectable({
    providedIn: 'root'
})
export class MapboxglFeatureInfoControl {

    private container: any;
    private isActive = false;
    public themeName: string;
    public tableDatas: any[];
    public button: HTMLButtonElement;
    public mapboxglService: MapboxGlService;
    public dialogRef: MatDialogRef<any> = null;
    private icon: any;

    constructor(
        {mapboxglService}
    ) {
        this.eventClick = this.eventClick.bind(this);
        this.mapboxglService = mapboxglService;
        this.mapboxglService.controlOnLoadedSubject.subscribe(control => {
            if (control !== ToolTypes.feature_info) {
                this.isActive = false;
                if (this.dialogRef) {
                    this.dialogRef.close();
                }
                if (this.button) {
                    this.button.classList.remove('active');
                    this.icon.style.color = 'black';
                }
                this.removeHighlightLayer().then();
            }
        });
        this.mapboxglService.mapOnClickedSubject.subscribe(e => {
            this.executeEvent(e).then();
        });
    }

    /**
     * Hàm lấy features và gọi modal
     * @param e Event point
     * @param eventType Click or hovers
     * @returns Void
     */
    async executeEvent(e): Promise<void> {

        // Stop if button not active
        if (!this.isActive) {
            return;
        }

        const uniqueKeys = new Set();
        const listFeatures = [];
        const features = await this.mapboxglService.getMapFeature(e);
        for (const featureItem of features) {
            const feature = featureItem;
            const properties = feature.properties;
            if (mapFields[feature.source]) {
                const ten = mapFields[feature.source].title;
                const click = mapFields[feature.source].click;
                const fields = mapFields[feature.source].fields;
                if (click) {
                    const data: any = [];
                    Object.keys(properties).forEach(key => {
                        if (fields[key] && properties[key]) {
                            if (!uniqueKeys.has(properties[key])) {
                                uniqueKeys.add(properties[key]);
                                data.push({
                                    label: fields[key],
                                    text: isNumber(properties[key]) ? Number(properties[key]).toLocaleString('en') : properties[key]
                                });
                            }
                        }
                    });
                    listFeatures.push({ ten, data });
                    // Highlight the feature
                    if (feature.layer.id !== 'Dibrugarh_boundary-default-fill') {
                        await this.highlightFeature(feature);
                    }
                }
            }
        }
        this.calculatingFeatureInfoModal(e, listFeatures, 'light');
    }

    async highlightFeature(feature: any): Promise<void> {
        // Remove any existing highlight layer
        await this.removeHighlightLayer();

        // Check if feature has a valid geometry
        if (feature.geometry) {
            const highlight = {
                id: 'highlight',
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        properties: {},
                        geometry: feature.geometry
                    }
                ]
            };
            this.mapboxglService.map.addSource('highlight-source', {
                type: 'geojson',
                data: highlight
            });
            this.mapboxglService.map.addLayer({
                id: 'highlight',
                type: 'fill',
                source: 'highlight-source',
                paint: {
                    'fill-color': '#DFBA36',
                    'fill-outline-color': '#ff0000',
                    'fill-opacity': 0.5
                }
            });
            const bbox = turf.bbox(highlight);
            this.mapboxglService.map.fitBounds(bbox,
                {
                    padding: 0,
                    maxZoom: 11
                });
        } else {
            console.error('Feature does not have a valid geometry:', feature);
        }
    }

    async removeHighlightLayer(): Promise<void> {
        if (this.mapboxglService.map.getLayer('highlight')) {
            this.mapboxglService.map.removeLayer('highlight');
        }
        if (this.mapboxglService.map.getSource('highlight-source')) {
            this.mapboxglService.map.removeSource('highlight-source');
        }
    }

    /**
     * Tính toán và hiển thị popup tại vị trí click
     * @param e Event point click
     * @param tableDatas Data table
     * @param themeName Theme
     */
    calculatingFeatureInfoModal(e, tableDatas, themeName): void {
        if (!tableDatas.length) {
            return;
        }
        if (this.dialogRef) {
            this.dialogRef.close();
        }

        // Set table props
        this.themeName = themeName;
        this.tableDatas = tableDatas;

        this.dialogRef = this.mapboxglService.dialog.open(
            MapboxglFeatureInfoComponent,
            {
                data: this,
                height: `${featureInfoPopupHeight}px`,
                width: `${featureInfoPopupWidth}px`,
                hasBackdrop: false,
                panelClass: 'mapbox-feature_info'
            }
        );

        const dialogPosition: any = {};
        const toadoX = e.originalEvent.clientX;
        const toadoY = e.originalEvent.clientY;

        dialogPosition.top = `${toadoY}px`;
        dialogPosition.left = `${toadoX - featureInfoPopupWidth / 2}px`;

        if (toadoX < featureInfoPopupWidth) {
            console.log('click sát mép trái');
            dialogPosition.left = `${toadoX}px`;
        }

        if (toadoX > window.innerWidth - featureInfoPopupWidth / 2) {
            console.log('click sát mép phải');
            dialogPosition.left = `${toadoX - featureInfoPopupWidth}px`;
        }

        if (toadoY > window.innerHeight - featureInfoPopupHeight) {
            console.log('click sát mép dưới');
            dialogPosition.top = `${toadoY - featureInfoPopupHeight}px`;
        }

        this.dialogRef.updatePosition(dialogPosition);
    }

    eventClick(e): void {
        this.button = e.currentTarget;
        this.button.classList.add('active');
        this.icon = this.button.querySelector<HTMLElement>('.material-icons-outlined');
        if (!this.isActive) {
            this.isActive = true;
            this.icon.style.color = 'red';
            this.mapboxglService.controlOnLoadedSubject.next(ToolTypes.feature_info);
        } else if (this.isActive) {
            this.isActive = false;
            this.button.classList.remove('active');
            this.icon.style.color = 'black';
            if (this.dialogRef) {
                this.dialogRef.close();
            }
            this.removeHighlightLayer().then();
        }
    }

    onAdd(): any {
        const featureToolInfo: TOOL = {
            name: 'Feature info',
            // iconClass: 'esri-icon esri-icon-description',
            iconClass: 'material-icons-outlined',
            code: 'FEATURE_TOOL',
            active: false,
            className: 'info'
        };

        const featureTool = this.mapboxglService.utilityFn.createTool(featureToolInfo, this.eventClick);
        this.container = this.mapboxglService.utilityFn.createControl([featureTool], 'mapbox-gl-feature_info');

        return this.container;
    }

    onRemove(): void {
        this.container.parentNode.removeChild(this.container);
    }

}
